import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useRememberListUrl } from '../lib/listReturn';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth, canDelete, can } from '../lib/auth';
import { ROLE } from '../lib/permissions';

interface PORow {
  id: string; poNumber: string; jobName: string; client: string;
  poDate: string; quantity: string; pricePerBox: string; finalAmount: string;
  orderType: string; isCancelled?: boolean; jobCardedQty?: number;
  cancelReason?: string;
  /** The PO's latest job card, when it was closed. */
  jobClosed?: { jobId: string; reason: string; on: string | null; by: string } | null;
}

const closedOn = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
interface ListResp { rows: PORow[]; total: number; page: number; pageSize: number }

const PAGE_SIZE = 50;

export function PurchaseOrdersPage() {
  useRememberListUrl();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get('page') ?? '1'));
  const q = params.get('q') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const [search, setSearch] = useState(q);
  const [fromInput, setFromInput] = useState(from);
  const [toInput, setToInput] = useState(to);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [closingId, setClosingId] = useState<string | null>(null);
  const [closeReason, setCloseReason] = useState('');
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['pos', { page, q, from, to }],
    queryFn: () => {
      const sp = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      if (q) sp.set('q', q);
      if (from) sp.set('from', from);
      if (to) sp.set('to', to);
      return api<ListResp>(`/api/purchase-orders?${sp.toString()}`);
    },
  });

  const { user } = useAuth();
  const isSuperAdmin = user?.rollId === ROLE.SuperAdmin;
  const del = useMutation({
    mutationFn: (ids: string[]) =>
      api('/api/purchase-orders/delete', { method: 'POST', body: JSON.stringify({ ids }) }),
    onSuccess: () => { setSelected(new Set()); qc.invalidateQueries({ queryKey: ['pos'] }); },
    onError: (e: Error) => {
      const m = e.message;
      if (m.startsWith('has_active_job:')) { const parts = m.split(':'); alert(`Cannot delete PO ${parts[1]} — Job Card ${parts[2]} exists. Delete or close the job first.`); }
      else if (m.startsWith('has_dispatches:')) alert(`Cannot delete — dispatches exist for ${m.split(':')[1]}.`);
      else alert(m);
    },
  });
  const closePo = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api(`/api/purchase-orders/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),
    onSuccess: () => { setClosingId(null); setCloseReason(''); qc.invalidateQueries({ queryKey: ['pos'] }); },
  });
  const reopenPo = useMutation({
    mutationFn: (id: string) =>
      api(`/api/purchase-orders/${id}/reopen`, { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pos'] }),
  });

  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    const p = new URLSearchParams();
    if (search) p.set('q', search);
    if (fromInput) p.set('from', fromInput);
    if (toInput) p.set('to', toInput);
    p.set('page', '1');
    setParams(p);
  }
  function goPage(next: number) {
    const p = new URLSearchParams(params);
    p.set('page', String(next));
    setParams(p);
  }
  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-brand">Purchase Orders</h1>
          <p className="text-xs text-slate-500">{total.toLocaleString()} active</p>
        </div>
        {can(user, 'CREATE_PO') && (
          <Link to="/purchase-orders/new" className="btn-primary">+ New PO</Link>
        )}
      </div>

      <form className="flex flex-wrap items-end gap-2" onSubmit={applySearch}>
        <div className="flex-1 min-w-[240px]">
          <label className="label">Search</label>
          <input className="input" placeholder="PO #, Job Name, Artwork, ID…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div>
          <label className="label">From</label>
          <input type="date" className="input" value={fromInput} onChange={(e) => setFromInput(e.target.value)} />
        </div>
        <div>
          <label className="label">To</label>
          <input type="date" className="input" value={toInput} onChange={(e) => setToInput(e.target.value)} />
        </div>
        <button className="btn-primary" type="submit">Search</button>
        {(q || from || to) && (
          <button type="button" className="btn-ghost"
            onClick={() => { setSearch(''); setFromInput(''); setToInput(''); setParams({}); }}>
            Reset
          </button>
        )}
        {canDelete(user) && selected.size > 0 && (
          <button type="button" className="btn-danger" disabled={del.isPending}
            onClick={() => { if (confirm(`Delete ${selected.size}?`)) del.mutate(Array.from(selected)); }}>
            Delete {selected.size}
          </button>
        )}
      </form>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2 w-8"></th>
                <th className="px-3 py-2">PO ID</th>
                <th className="px-3 py-2">PO #</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Job Name</th>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">PO Date</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-right">Price/Box</th>
                <th className="px-3 py-2 text-right">Final</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading && <tr><td colSpan={11} className="p-8 text-center text-slate-500">Loading…</td></tr>}
              {list.isError && <tr><td colSpan={11} className="p-8 text-center text-red-600">Failed: {(list.error as Error).message}</td></tr>}
              {!list.isLoading && rows.length === 0 && <tr><td colSpan={11} className="p-8 text-center text-slate-500">No records.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} /></td>
                  <td className="px-3 py-2 font-mono text-xs">{r.id}</td>
                  <td className="px-3 py-2">
                    {r.poNumber}
                    {r.isCancelled && (
                      <div className="mt-1 max-w-[260px] rounded bg-red-50 px-1.5 py-0.5 text-[11px] leading-snug text-red-700">
                        <span className="font-semibold">PO closed</span>{r.cancelReason ? ` - ${r.cancelReason}` : ''}
                      </div>
                    )}
                    {r.jobClosed && (
                      <div className="mt-1 max-w-[260px] rounded bg-amber-50 px-1.5 py-0.5 text-[11px] leading-snug text-amber-800"
                           title={r.jobClosed.jobId}>
                        <span className="font-semibold">Job card {r.jobClosed.jobId.split('/').pop()} closed</span>
                        {r.jobClosed.reason ? ` - ${r.jobClosed.reason}` : ' - no reason recorded'}
                        {(r.jobClosed.by || r.jobClosed.on) && (
                          <span className="text-amber-700/80"> ({[r.jobClosed.by, closedOn(r.jobClosed.on)].filter(Boolean).join(', ')})</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{r.orderType}</td>
                  <td className="px-3 py-2">{r.jobName}</td>
                  <td className="px-3 py-2 text-slate-600">{r.client}</td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{r.poDate}</td>
                  <td className="px-3 py-2 text-right">{r.quantity}</td>
                  <td className="px-3 py-2 text-right">{r.pricePerBox}</td>
                  <td className="px-3 py-2 text-right font-medium">{r.finalAmount}</td>
                  {(() => {
                    const ordered = Number(r.quantity) || 0;
                    const jcQty = r.jobCardedQty ?? 0;
                    const fullyConsumed = ordered > 0 && jcQty >= ordered;
                    // Print for everyone who can see the list; Edit for those who can edit.
                    return (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {can(user, 'EDIT_PO') && <Link to={`/purchase-orders/${r.id}/edit`} className="mr-2 text-brand hover:underline text-sm">Edit</Link>}
                        <Link to={`/purchase-orders/${encodeURIComponent(r.id)}/print`} className="text-slate-600 hover:underline text-sm" title="Print or save as PDF">Print</Link>
                        {isSuperAdmin && !r.isCancelled && !fullyConsumed && (
                          <button className="ml-2 text-red-600 hover:underline text-sm" onClick={() => {
                            setClosingId(r.id); setCloseReason('');
                          }}>Close</button>
                        )}
                        {isSuperAdmin && r.isCancelled && (
                          <button className="ml-2 text-green-600 hover:underline text-sm" onClick={() => reopenPo.mutate(r.id)}>Reopen</button>
                        )}
                      </td>
                    );
                  })()}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="text-slate-500">Page {page} of {totalPages.toLocaleString()}</div>
        <div className="flex gap-2">
          <button className="btn-ghost" disabled={page <= 1} onClick={() => goPage(page - 1)}>‹ Prev</button>
          <button className="btn-ghost" disabled={page >= totalPages} onClick={() => goPage(page + 1)}>Next ›</button>
        </div>
      </div>

      {closingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-800 mb-1">Close Purchase Order</h3>
            <p className="text-sm text-slate-500 mb-4">PO {closingId} will be closed and no new job cards can be created against it.</p>
            <label className="label">Reason *</label>
            <textarea
              className="input w-full"
              rows={3}
              placeholder="Why is this PO being closed?"
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              autoFocus
            />
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => { setClosingId(null); setCloseReason(''); }}>Cancel</button>
              <button
                className="rounded bg-red-600 px-4 py-2 text-sm text-white font-medium hover:bg-red-700 disabled:opacity-50"
                disabled={!closeReason.trim() || closePo.isPending}
                onClick={() => closePo.mutate({ id: closingId, reason: closeReason.trim() })}
              >
                {closePo.isPending ? 'Closing…' : 'Close PO'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
